
import { Github, Linkedin, Twitter, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: "AI Solutions", href: "/services" },
      { name: "ERP Systems", href: "/services" },
      { name: "eCommerce", href: "/services" },
      { name: "System Integration", href: "/services" },
      { name: "Custom Development", href: "/services" },
    ],
    company: [
      { name: "About", href: "/about" },
      { name: "Portfolio", href: "/portfolio" },
      { name: "Careers", href: "/careers" },
      { name: "Contact", href: "/contact" },
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
    ],
  };

  const socialLinks = [
    { name: "GitHub", icon: Github, href: "#" },
    { name: "LinkedIn", icon: Linkedin, href: "#" },
    { name: "Twitter", icon: Twitter, href: "#" },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-white/5">
      {/* Background */}
      <div className="absolute inset-0 bg-zinc-950" />
      <div className="absolute inset-0 mesh-gradient-2 opacity-30" />

      {/* Top Section */}
      <div className="relative z-10 container mx-auto px-6 pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block group">
              <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-gradient-gold transition-all duration-300">
                FortifyTek
              </span>
            </Link>

            <p className="mt-6 text-zinc-400 leading-relaxed max-w-sm">
              End-to-end enterprise solutions engineered for scale, security, and seamless integration. Building what others can't since 2020.
            </p>

            {/* Social Links */}
            <div className="mt-8 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-amber-500/30 hover:bg-amber-500/10 transition-all duration-300"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Newsletter / CTA */}
            <div className="mt-10 glass-panel-warm rounded-2xl p-6">
              <p className="text-white font-semibold mb-2">Ready to transform your business?</p>
              <p className="text-sm text-zinc-400 mb-4">Let's discuss your project requirements.</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-amber-400 font-semibold hover:text-amber-300 transition-colors group"
              >
                Get in touch
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Links Columns */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {/* Services */}
              <div>
                <h4 className="label-mono text-zinc-500 mb-6">Services</h4>
                <ul className="space-y-4">
                  {footerLinks.services.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="label-mono text-zinc-500 mb-6">Company</h4>
                <ul className="space-y-4">
                  {footerLinks.company.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="label-mono text-zinc-500 mb-6">Contact</h4>
                <ul className="space-y-4 text-sm">
                  <li>
                    <a
                      href="mailto:ftsupport@fortifytek.com"
                      className="text-zinc-400 hover:text-amber-400 transition-colors duration-300"
                    >
                      ftsupport@fortifytek.com
                    </a>
                  </li>
                  <li>
                    <a
                      href="tel:+14242424140"
                      className="text-zinc-400 hover:text-amber-400 transition-colors duration-300"
                    >
                      +1 (424) 242-4140
                    </a>
                  </li>
                  <li className="text-zinc-500">
                    Riverside, CA, USA
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="relative z-10 border-t border-white/5">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-zinc-500">
              &copy; {currentYear} FortifyTek. All rights reserved.
            </p>

            <div className="flex items-center gap-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm text-zinc-500 hover:text-white transition-colors duration-300"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Element */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
    </footer>
  );
};
